import { DOMAIN_BRANDING, getBranding } from './domainBranding';
import { normalizeHostname } from './domainAccess';

/**
 * Per-domain titles, descriptions, icons and Open Graph for link previews.
 * Crawlers need absolute og:image URLs — use metadataBase + path.
 */

const SHARE = {
  leadscale: {
    siteName: 'LeadScale',
    title: 'LeadScale — Global Partner Network',
    description:
      'Connect affiliates with high-potential offers and advertisers with qualified lead sources across global markets.',
    ogImage: '/og/leadscale-og.png',
    themeColor: '#07131f',
    tileColor: '#07131f',
    canonicalHost: 'app.leadscaleglobal.com',
    manifest: '/leadscale-icons/site.webmanifest',
  },
  affland: {
    siteName: 'Affland',
    title: 'Affland — Affiliate Network',
    description:
      'Work with Affland for high payouts, author offers, regular payments, and professional support.',
    ogImage: '/og/affland-og.png',
    themeColor: '#ffffff',
    tileColor: '#28a745',
    canonicalHost: 'partner.affland.id',
    manifest: '/affland-icons/site.webmanifest',
  },
  exodo: {
    siteName: 'Exodo Panel',
    title: 'Exodo Panel',
    description: 'Exodo operations panel for orders, partners, and commerce tools.',
    ogImage: '/og/exodo-og.png',
    themeColor: '#ffffff',
    tileColor: '#4f46e5',
    canonicalHost: 'exodopanel.com',
    manifest: '/site.webmanifest',
  },
  crm: {
    siteName: 'Exodo CRM',
    title: 'Exodo CRM',
    description: 'Exodo CRM workspace.',
    ogImage: '/og/exodo-og.png',
    themeColor: '#ffffff',
    tileColor: '#4f46e5',
    canonicalHost: 'crm.exodo.id',
    manifest: '/site.webmanifest',
  },
};

/**
 * @param {string} hostname
 * @returns {typeof SHARE.leadscale & { branding: object, origin: string, icons: object }}
 */
export function getDomainShareMeta(hostname) {
  const host = normalizeHostname(hostname);
  const branding = getBranding(host);
  const theme = branding.theme === 'leadscale'
    ? 'leadscale'
    : branding.theme === 'affland'
      ? 'affland'
      : branding.theme === 'crm'
        ? 'crm'
        : 'exodo';

  const share = SHARE[theme];
  const isDevHost = host === 'localhost' || host === '127.0.0.1';
  // Prefer real request host for local previews; production uses brand canonical host
  const publicHost = isDevHost
    ? host
    : host === 'advertiser.exodopanel.com'
      ? 'app.leadscaleglobal.com'
      : host === 'staging.exodopanel.com'
        ? 'staging.exodopanel.com'
        : share.canonicalHost || host;

  const protocol = isDevHost ? 'http' : 'https';
  const port = isDevHost ? ':3000' : '';
  const origin = `${protocol}://${publicHost}${port}`;

  const favicon16 = branding.favicon16 || '/favicon-16x16.png';
  const favicon32 = branding.favicon32 || branding.favicon || '/favicon-32x32.png';
  const apple = branding.appleTouchIcon || '/apple-touch-icon.png';

  return {
    ...share,
    branding,
    theme,
    host,
    origin,
    icons: {
      icon: [
        { url: favicon16, sizes: '16x16', type: 'image/png' },
        { url: favicon32, sizes: '32x32', type: 'image/png' },
        { url: branding.favicon || favicon32 },
      ],
      apple: [{ url: apple, sizes: '180x180', type: 'image/png' }],
    },
  };
}

/** Absolute OG image URL for a hostname. */
export function absoluteOgImage(hostname) {
  const meta = getDomainShareMeta(hostname);
  return `${meta.origin}${meta.ogImage}`;
}

export { SHARE, DOMAIN_BRANDING };
